import * as React from 'react';
import { useCurrentEntityId } from '../../../hooks/useSelectedEntity/useSelectedEntity';
import { gql } from '@apollo/client';
import {
  DocumentAnalysis,
  onUpdateDocumentAnalysis as ON_UPDATE_DOCUMENT_ANALYSIS,
  OnUpdateDocumentAnalysisSubscription,
} from '@admiin-com/ds-graphql';

import { useSubscriptionWithReconnect } from '../../../hooks/useSubscriptionWithReconnect/useSubscriptionWithReconnect';

import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';

interface Props {
  setScanning: (scanning: boolean) => void;
  updateBill: (documentAnalysis: DocumentAnalysis) => void;
}

/**
 * Refactored version using the reusable subscription hook
 * This demonstrates how much simpler the code becomes with the reusable hook
 */
export const useScanning = ({ setScanning, updateBill }: Props) => {
  const entityId = useEntityId();

  // Use the reusable subscription hook with reconnection logic
  const { isConnected, reconnectAttempts } =
    useSubscriptionWithReconnect<OnUpdateDocumentAnalysisSubscription>(
      gql(ON_UPDATE_DOCUMENT_ANALYSIS),
      {
        variables: {
          entityId: entityId,
        },
        fetchPolicy: 'network-only',
        skip: !entityId,

        // Handle incoming data
        onData: (data) => {
          const incomingAnalysis = data.data.data?.onUpdateDocumentAnalysis;

          if (!incomingAnalysis) {
            return;
          }

          // Update bills directly
          updateBill(incomingAnalysis);
        },

        // Handle errors (scanning state is set to false)
        onError: (err) => {
          console.error('Document analysis subscription error:', err);
          setScanning(false);
        },

        // Optional callbacks for monitoring reconnection
        onReconnect: () => {
          console.log(
            'Attempting to reconnect document analysis subscription...'
          );
        },

        onReconnectSuccess: () => {
          console.log(
            'Document analysis subscription reconnected successfully'
          );
        },

        // Customizable reconnection settings
        enableAutoReconnect: true,
        initialDelay: 500,
        maxDelay: 10000,
      }
    );

  // Optional: Expose connection state for UI feedback
  React.useEffect(() => {
    if (reconnectAttempts > 3) {
      console.warn(
        `Document analysis subscription struggling to connect (${reconnectAttempts} attempts)`
      );
    }
  }, [reconnectAttempts]);

  return {
    isConnected,
    reconnectAttempts,
  };
};
